// ============================================================
// src/pages/Settings.jsx
//
// ── WHAT THIS FILE DOES ──────────────────────────────────────
// Settings page for Drag Pocket Planner (React).
//
// Sections:
//   0. PROFILE          stage name, pronouns, location,
//                       hide earnings toggle, performer website
//   1. SOCIAL MEDIA     six handles used on PromoGenerator posters
//   2. PAYMENT          cashapp, venmo, paypal
//   3. MY LINKS PAGE    username picker + public links card
//   4. GIG NOTIFICATIONS full Web Push pipeline via OneSignal
//   5. GET READY TIMER  how long it takes to get ready
//   6. ADVANCE WARNING  heads-up before each notification fires
//   7. HOME LOCATION    travel origin (Nominatim / GPS / MapPicker)
//   8. PERFORMER ID     shareable UUID for producer cross-linking
//   9. ABOUT & LEGAL    version, About, Privacy Policy
//  10. ACCOUNT          sign out
//
// All form inputs use DPP CSS variables from index.css.
// Social/payment handle inputs use the shared handle-input pattern
// (prefix span + input) styled to match the DPP form-group spec.
//
// No Bootstrap utility classes (mb-3, fw-semibold, d-flex, etc.).
// No inline Bootstrap component classes (form-control, input-group).
// All layout and spacing uses CSS variables or minimal inline style.
// ============================================================

import { useState, useEffect, useCallback }      from 'react'
import { Link }                                   from 'react-router-dom'
import { useNotificationPrefs, LEAVE_BUFFER_MINUTES } from '../hooks/useNotificationPrefs'
import { usePushNotifications }                   from '../hooks/usePushNotifications'
import MapPicker     from '../components/MapPicker'
import ThemeSelector from '../components/ThemeSelector'
import { supabase }  from '../lib/supabaseClient'




// ── minutesToHM ───────────────────────────────────────────────
// Converts a total-minutes integer into { hours, minutes }.
// Used to split the prep and advance-warning totals for the
// separate hours/minutes number inputs.
function minutesToHM(total) {
  return { hours: Math.floor(total / 60), minutes: total % 60 }
}


// ── HandleInput ───────────────────────────────────────────────
// Reusable component for social / payment handle fields.
// Shows a styled prefix (e.g. "@", "$", "paypal.me/") beside
// a DPP-styled text input. Strips the prefix character if the
// user accidentally types it.
//
// Props:
//   id        (string)    input id, ties the label htmlFor
//   label     (ReactNode) label content (may include an icon)
//   prefix    (string)    display prefix, e.g. "@" or "$"
//   value     (string)    controlled value
//   onChange  (function)  receives the new stripped value string
//   strip     (string)    character(s) to auto-strip from the start,
//                         e.g. "@" or "$" — defaults to prefix
//   disabled  (boolean)   passed to the input
//   placeholder (string)  input placeholder
//   maxLength (number)    input maxLength
function HandleInput({
  id,
  label,
  prefix,
  value,
  onChange,
  strip,
  disabled,
  placeholder,
  maxLength = 60,
}) {
  // Default strip target is the prefix itself
  const stripChar = strip !== undefined ? strip : prefix

  // Build a regex that removes any leading occurrence of the strip char
  const handleChange = (e) => {
    const cleaned = e.target.value.replace(new RegExp(`^[${escapeRegex(stripChar)}]+`), '')
    onChange(cleaned)
  }

  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
        {/* Styled prefix badge that visually attaches to the input */}
        <span style={{
          display:         'flex',
          alignItems:      'center',
          padding:         '0 0.75rem',
          background:      'var(--pink-pale)',
          border:          '2px solid var(--accent-color)',
          borderRight:     'none',
          borderRadius:    'var(--radius-md) 0 0 var(--radius-md)',
          color:           'var(--neutral-1)',
          fontWeight:      700,
          fontSize:        '0.9rem',
          whiteSpace:      'nowrap',
          flexShrink:      0,
          userSelect:      'none',
        }}>
          {prefix}
        </span>
        <input
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{
            // Override the default border-radius so the left side is square
            // where it meets the prefix badge.
            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
            flex: 1,
          }}
        />
      </div>
    </div>
  )
}

// escapeRegex — escape special regex characters in a string so it
// can be used safely inside a RegExp constructor.
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}


// ── SectionCard ───────────────────────────────────────────────
// Wraps a settings section in the DPP card style with a heading.
function SectionCard({ title, children }) {
  return (
    <section style={{
      background:    'var(--brand-white)',
      border:        '3px solid var(--secondary)',
      borderRadius:  'var(--radius-card)',
      marginBottom:  'var(--spacing-lg)',
      overflow:      'hidden',
    }}>
      {/* Card header — hot pink left border accent */}
      <div style={{
        padding:        'var(--space-4) var(--spacing-lg)',
        borderBottom:   '2px solid var(--accent-color)',
        borderLeft:     '5px solid var(--primary)',
        background:     'var(--pink-pale)',
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize:   'var(--text-2xl)',
          color:      'var(--neutral-1)',
          margin:     0,
        }}>
          {title}
        </h2>
      </div>

      {/* Card body */}
      <div style={{ padding: 'var(--spacing-lg)' }}>
        {children}
      </div>
    </section>
  )
}


// ── SaveButton ────────────────────────────────────────────────
// Reusable save button with spinner state.
function SaveButton({ onClick, saving, disabled, children }) {
  return (
    <button
      className="btn btn-primary"
      type="button"
      onClick={onClick}
      disabled={saving || disabled}
    >
      {saving ? (
        <>
          <span
            aria-hidden="true"
            style={{
              display:      'inline-block',
              width:        '0.9em',
              height:       '0.9em',
              border:       '2px solid rgba(255,255,255,0.4)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation:    'spin 0.7s linear infinite',
              marginRight:  '0.4rem',
              verticalAlign: 'middle',
            }}
          />
          Saving...
        </>
      ) : (
        <>
          <i className="bi bi-floppy" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
          {children}
        </>
      )}
    </button>
  )
}


// ── AlertBanner ───────────────────────────────────────────────
// Inline success / error feedback strip.
function AlertBanner({ type, children }) {
  // type: 'success' | 'danger' | 'warning' | 'info'
  const colors = {
    success: { bg: 'rgba(76,175,80,0.1)',   border: 'var(--tertiary-1)',      icon: 'bi-check-circle-fill',      iconColor: 'var(--tertiary-1-dark)' },
    danger:  { bg: 'rgba(255,68,68,0.1)',   border: 'var(--tertiary-3)',      icon: 'bi-exclamation-circle-fill', iconColor: 'var(--tertiary-3-dark)' },
    warning: { bg: 'rgba(255,152,0,0.1)',   border: 'var(--tertiary-2)',      icon: 'bi-exclamation-triangle-fill', iconColor: 'var(--tertiary-2-dark)' },
    info:    { bg: 'rgba(255,255,51,0.15)', border: 'var(--tertiary-4)',      icon: 'bi-info-circle-fill',       iconColor: 'var(--tertiary-2-dark)' },
  }
  const c = colors[type] || colors.info

  return (
    <div style={{
      display:      'flex',
      alignItems:   'flex-start',
      gap:          '0.5rem',
      background:   c.bg,
      border:       `2px solid ${c.border}`,
      borderRadius: 'var(--radius-md)',
      padding:      '0.6rem 0.9rem',
      fontSize:     'var(--text-sm)',
      color:        'var(--neutral-1)',
      marginBottom: 'var(--spacing-md)',
    }}>
      <i
        className={`bi ${c.icon}`}
        aria-hidden="true"
        style={{ color: c.iconColor, flexShrink: 0, marginTop: '0.1rem' }}
      />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}


// ── ToggleRow ─────────────────────────────────────────────────
// A labeled row with a DPP switch toggle on the right.
// Props:
//   checked   (boolean)
//   onChange  (function)
//   disabled  (boolean)
//   label     (ReactNode)
//   description (string)
function ToggleRow({ checked, onChange, disabled, label, description }) {
  return (
    <div style={{
      display:      'flex',
      alignItems:   'center',
      justifyContent: 'space-between',
      background:   'var(--neutral-bg)',
      border:       '2px solid var(--accent-color)',
      borderRadius: 'var(--radius-md)',
      padding:      'var(--spacing-md)',
      gap:          '1rem',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: 'var(--neutral-1)', marginBottom: '0.2rem' }}>
          {label}
        </div>
        {description && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.65 }}>
            {description}
          </div>
        )}
      </div>
      {/* DPP switch — uses .switch / .slider classes from index.css */}
      <label className="switch" style={{ flexShrink: 0 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
        <span className="slider" />
      </label>
    </div>
  )
}


// ── NumberPairInput ───────────────────────────────────────────
// Two number inputs (hours + minutes) side by side.
// Used for the prep time and advance warning sections.
function NumberPairInput({
  hoursValue,
  minsValue,
  onHoursChange,
  onMinsChange,
  maxHours = 8,
  disabled,
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <input
        type="number"
        min={0}
        max={maxHours}
        value={hoursValue}
        onChange={onHoursChange}
        disabled={disabled}
        style={{ width: '80px' }}
      />
      <span style={{ color: 'var(--neutral-1)', opacity: 0.75, fontWeight: 600, fontSize: 'var(--text-sm)' }}>
        hr
      </span>
      <input
        type="number"
        min={0}
        max={59}
        value={minsValue}
        onChange={onMinsChange}
        disabled={disabled}
        style={{ width: '80px' }}
      />
      <span style={{ color: 'var(--neutral-1)', opacity: 0.75, fontWeight: 600, fontSize: 'var(--text-sm)' }}>
        min
      </span>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function Settings() {

  // ── PROFILE ───────────────────────────────────────────────
  const [stageName, setStageName] = useState('')
  const [pronouns,  setPronouns]  = useState('')
  const [location,  setLocation]  = useState('')
  const [website,   setWebsite]   = useState('')

  // ── HIDE EARNINGS ─────────────────────────────────────────
  // Stored as user_metadata.hide_earnings (boolean, default false).
  // When true, Dashboard and GigList suppress fee / payment display.
  // Full details remain visible only on the Earnings page.
  const [hideEarnings, setHideEarnings] = useState(false)

  // ── SOCIAL HANDLES ─────────────────────────────────────────
  const [instagram, setInstagram] = useState('')
  const [twitterX,  setTwitterX]  = useState('')
  const [tiktok,    setTiktok]    = useState('')
  const [bluesky,   setBluesky]   = useState('')
  const [youtube,   setYoutube]   = useState('')
  const [patreon,   setPatreon]   = useState('')

  // ── PAYMENT HANDLES ──────────────────────────────────────
  const [cashapp, setCashapp] = useState('')
  const [venmo,   setVenmo]   = useState('')
  const [paypal,  setPaypal]  = useState('')

  // ── SHARED PROFILE SAVE STATE ─────────────────────────────
  // One save function covers Profile, Social, and Payment sections
  // because they all target the same Supabase call.
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSaved,  setProfileSaved]  = useState(false)
  const [profileError,  setProfileError]  = useState(null)

  // ── AUTH / IDENTITY ───────────────────────────────────────
  const [userEmail, setUserEmail] = useState('')
  const [userID,    setUserID]    = useState('')
  const [idCopied,  setIdCopied]  = useState(false)

  // ── USERNAME ──────────────────────────────────────────────
  // Stored in public.profiles.username (text UNIQUE).
  // Needs migration: ALTER TABLE public.profiles ADD COLUMN username TEXT UNIQUE;
  // Human-friendly public URL: /fanlinks/:username
  // Falls back to /fanlinks/:uuid when not set.
  const [username,          setUsername]          = useState('')
  const [usernameAvailable, setUsernameAvailable] = useState(null) // null | true | false
  const [usernameChecking,  setUsernameChecking]  = useState(false)
  const [usernameSaving,    setUsernameSaving]    = useState(false)
  const [usernameSaved,     setUsernameSaved]     = useState(false)
  const [usernameError,     setUsernameError]     = useState(null)
  const [linkCopied,        setLinkCopied]        = useState(false)
  const [usernameTimer,     setUsernameTimer]     = useState(null)


  // ── LOAD PROFILE + SOCIALS FROM USER_METADATA ─────────────
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return
      const m = user.user_metadata || {}

      // Profile fields
      setStageName(m.performer_name || '')
      setPronouns(m.pronouns        || '')
      setLocation(m.location        || '')
      setWebsite(m.website          || '')
      setHideEarnings(!!m.hide_earnings)

      // Social handles
      setInstagram(m.instagram  || '')
      setTwitterX(m.twitter_x   || '')
      setTiktok(m.tiktok        || '')
      setBluesky(m.bluesky      || '')
      setYoutube(m.youtube      || '')
      setPatreon(m.patreon      || '')

      // Payment handles
      setCashapp(m.cashapp || '')
      setVenmo(m.venmo     || '')
      setPaypal(m.paypal   || '')

      // Identity
      setUserEmail(user.email || '')
      setUserID(user.id       || '')

      // Username lives in public.profiles, not user_metadata,
      // because it requires a UNIQUE constraint across all users.
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single()
      if (profileData) setUsername(profileData.username || '')
    })
  }, [])


  // ── SAVE PROFILE + SOCIAL + PAYMENT ──────────────────────
  // One call covers all three sections. Username is saved separately
  // via its own button and saveUsername() below.
  const saveProfile = useCallback(async () => {
    setProfileSaving(true)
    setProfileError(null)
    setProfileSaved(false)

    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          performer_name: stageName.trim(),
          pronouns:       pronouns.trim(),
          location:       location.trim(),
          website:        website.trim(),
          hide_earnings:  hideEarnings,
          // Strip leading sigils so they are not double-stored
          instagram: instagram.trim().replace(/^@/, ''),
          twitter_x: twitterX.trim().replace(/^@/, ''),
          tiktok:    tiktok.trim().replace(/^@/, ''),
          bluesky:   bluesky.trim().replace(/^@/, ''),
          youtube:   youtube.trim().replace(/^@/, ''),
          patreon:   patreon.trim().replace(/^@/, ''),
          cashapp:   cashapp.trim().replace(/^\$/, ''),
          venmo:     venmo.trim().replace(/^@/, ''),
          paypal:    paypal.trim().replace(/^@/, ''),
        },
      })
      if (error) throw error

      // Sync the same data to public.profiles so the public links
      // page can read it without requiring auth.
      // Username is intentionally excluded here — it goes via saveUsername().
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      const { error: syncError } = await supabase
        .from('profiles')
        .upsert(
          {
            id:             currentUser.id,
            performer_name: stageName.trim()                   || null,
            email:          currentUser.email                  || null,
            pronouns:       pronouns.trim()                    || null,
            location:       location.trim()                    || null,
            website:        website.trim()                     || null,
            instagram:      instagram.trim().replace(/^@/, '') || null,
            twitter_handle: twitterX.trim().replace(/^@/, '')  || null,
            tiktok:         tiktok.trim().replace(/^@/, '')    || null,
            bluesky:        bluesky.trim().replace(/^@/, '')   || null,
            youtube:        youtube.trim().replace(/^@/, '')   || null,
            patreon:        patreon.trim().replace(/^@/, '')   || null,
            cashapp:        cashapp.trim().replace(/^\$/, '')  || null,
            venmo:          venmo.trim().replace(/^@/, '')     || null,
            paypal:         paypal.trim().replace(/^@/, '')    || null,
          },
          { onConflict: 'id' }
        )

      if (syncError) {
        // Sync failure is non-fatal — auth update already succeeded
        console.warn('Settings: profiles sync warning:', syncError.message)
      }

      setProfileSaved(true)
      setTimeout(() => setProfileSaved(false), 3000)

    } catch (err) {
      console.error('Settings: saveProfile error:', err)
      setProfileError(err.message || 'Failed to save profile.')
    } finally {
      setProfileSaving(false)
    }
  }, [
    stageName, pronouns, location, website, hideEarnings,
    instagram, twitterX, tiktok, bluesky, youtube, patreon,
    cashapp, venmo, paypal,
  ])


  // ── USERNAME AVAILABILITY CHECK ───────────────────────────
  // Fires after 500ms of idle typing. Strips to safe characters
  // (lowercase alphanumeric, hyphen, underscore) in real time.
  const checkUsername = useCallback(async (clean) => {
    setUsernameAvailable(null)
    setUsernameError(null)
    if (clean.length < 3) return

    setUsernameChecking(true)
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', clean)
        .neq('id', userID)
        .maybeSingle()
      // maybeSingle returns null data (not error) when no row is found
      setUsernameAvailable(!data)
    } catch (err) {
      console.warn('Settings: username check error:', err)
    } finally {
      setUsernameChecking(false)
    }
  }, [userID])

  const handleUsernameChange = (e) => {
    // Apply character filter instantly so the field stays clean as you type
    const clean = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
    setUsername(clean)
    setUsernameAvailable(null)
    if (usernameTimer) clearTimeout(usernameTimer)
    if (clean.length >= 3) {
      setUsernameTimer(setTimeout(() => checkUsername(clean), 500))
    }
  }

  // ── SAVE USERNAME ─────────────────────────────────────────
  const saveUsername = useCallback(async () => {
    if (!username.trim() || username.trim().length < 3) {
      setUsernameError('Username must be at least 3 characters.')
      return
    }
    if (usernameAvailable === false) {
      setUsernameError('That username is already taken.')
      return
    }
    setUsernameSaving(true)
    setUsernameError(null)
    setUsernameSaved(false)

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: username.trim() })
        .eq('id', userID)
      if (error) throw error
      setUsernameSaved(true)
      setUsernameAvailable(null) // reset check indicator after save
      setTimeout(() => setUsernameSaved(false), 3000)
    } catch (err) {
      console.error('Settings: saveUsername error:', err)
      setUsernameError(
        err.code === '23505'
          ? 'That username was just taken. Try another.'
          : err.message || 'Failed to save username.'
      )
    } finally {
      setUsernameSaving(false)
    }
  }, [username, userID, usernameAvailable])

  // ── PUBLIC URL + COPY HELPERS ─────────────────────────────
  const publicSlug = username || userID
  const publicUrl  = `${window.location.origin}/fanlinks/${publicSlug}`

  const handleCopyLink = useCallback(() => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(publicUrl)
    } else {
      const el = document.createElement('textarea')
      el.value = publicUrl
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 1500)
  }, [publicUrl])

  const handleCopyID = useCallback(() => {
    if (!userID) return
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(userID)
    } else {
      const el = document.createElement('textarea')
      el.value = userID
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setIdCopied(true)
    setTimeout(() => setIdCopied(false), 1500)
  }, [userID])


  // ── PUSH NOTIFICATIONS ─────────────────────────────────────
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading:            pushLoading,
    error:                pushError,
    subscribe,
    unsubscribe,
    sendTestNotification,
  } = usePushNotifications()

  const isIOS        = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isStandalone = (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  )


  // ── NOTIFICATION PREFS ─────────────────────────────────────
  const {
    prepMinutes,
    setPrepMinutes,
    advanceNoticeMinutes,
    setAdvanceNoticeMinutes,
    homeAddress,
    setHomeAddress,
    homeLat,
    homeLng,
    geocodedLabel,
    setHomeLat,
    setHomeLng,
    setGeocodedLabel,
    loading:          prefsLoading,
    saving:           prefsSaving,
    geocoding,
    error:            prefsError,
    saveSuccess,
    geocodeAndPreview,
    useCurrentLocation,
    savePrefs,
  } = useNotificationPrefs()


  // ── PREP TIME DROPDOWNS ────────────────────────────────────
  const hm = minutesToHM(prepMinutes)
  const [prepHours, setPrepHours] = useState(hm.hours)
  const [prepMins,  setPrepMins]  = useState(hm.minutes)

  // Sync local hours/mins state when the hook value changes
  // (e.g. on initial load from Supabase)
  useEffect(() => {
    const { hours, minutes } = minutesToHM(prepMinutes)
    setPrepHours(hours)
    setPrepMins(minutes)
  }, [prepMinutes])

  function handlePrepHoursChange(e) {
    const h = Math.min(8, Math.max(0, parseInt(e.target.value, 10) || 0))
    setPrepHours(h)
    setPrepMinutes(h * 60 + prepMins)
  }
  function handlePrepMinsChange(e) {
    const m = Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
    setPrepMins(m)
    setPrepMinutes(prepHours * 60 + m)
  }


  // ── ADVANCE WARNING DROPDOWNS ──────────────────────────────
  const ahm = minutesToHM(advanceNoticeMinutes)
  const [advanceHours, setAdvanceHours] = useState(ahm.hours)
  const [advanceMins,  setAdvanceMins]  = useState(ahm.minutes)

  useEffect(() => {
    const { hours, minutes } = minutesToHM(advanceNoticeMinutes)
    setAdvanceHours(hours)
    setAdvanceMins(minutes)
  }, [advanceNoticeMinutes])

  function handleAdvanceHoursChange(e) {
    const h = Math.min(2, Math.max(0, parseInt(e.target.value, 10) || 0))
    setAdvanceHours(h)
    setAdvanceNoticeMinutes(h * 60 + advanceMins)
  }
  function handleAdvanceMinsChange(e) {
    const m = Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
    setAdvanceMins(m)
    setAdvanceNoticeMinutes(advanceHours * 60 + m)
  }


  // ── MAP PIN MOVE ──────────────────────────────────────────
  const handleMapLocationChange = useCallback(({ lat, lng, address }) => {
    setHomeLat(lat)
    setHomeLng(lng)
    if (address) {
      setHomeAddress(address)
      setGeocodedLabel(address)
    }
  }, [setHomeLat, setHomeLng, setHomeAddress, setGeocodedLabel])


  // ── RENDER ────────────────────────────────────────────────
  return (
    <div className="settings-page-wrapper">

      <h1 style={{ marginBottom: 'var(--spacing-xl)' }}>Settings</h1>


      {/* ── 0. PROFILE ─────────────────────────────────────── */}
      <SectionCard title="Profile">

        {/* Stage Name */}
        <div className="form-group">
          <label htmlFor="stageNameInput">Stage Name</label>
          <input
            id="stageNameInput"
            type="text"
            placeholder="Your performer name"
            value={stageName}
            onChange={e => setStageName(e.target.value)}
            disabled={profileSaving}
            maxLength={80}
          />
        </div>

        {/* Email — read-only */}
        <div className="form-group">
          <label htmlFor="emailInput">Email</label>
          <input
            id="emailInput"
            type="email"
            value={userEmail}
            disabled
          />
          <small>Email cannot be changed here.</small>
        </div>

        {/* Stage Pronouns */}
        <div className="form-group">
          <label htmlFor="pronounsInput">Stage Pronouns</label>
          <input
            id="pronounsInput"
            type="text"
            placeholder="e.g. they/them, she/her, he/him"
            value={pronouns}
            onChange={e => setPronouns(e.target.value)}
            disabled={profileSaving}
            maxLength={40}
          />
        </div>

        {/* Location */}
        <div className="form-group">
          <label htmlFor="locationInput">Location You are Based Out Of</label>
          <input
            id="locationInput"
            type="text"
            placeholder="City, State"
            value={location}
            onChange={e => setLocation(e.target.value)}
            disabled={profileSaving}
            maxLength={80}
          />
        </div>

        {/* Performer Website */}
        <div className="form-group">
          <label htmlFor="websiteInput">Performer Website</label>
          <input
            id="websiteInput"
            type="url"
            placeholder="https://yoursite.com"
            value={website}
            onChange={e => setWebsite(e.target.value)}
            disabled={profileSaving}
            maxLength={200}
          />
          <small>Shown as a button on your public links page.</small>
        </div>

        {/* Hide Earnings Toggle */}
        <div className="form-group">
          <label>
            <i className="bi bi-eye-slash" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
            Hide Earnings
          </label>
          <small style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
            When on, pay rates, fees, and earnings totals are hidden on the Dashboard and Gig
            List. All earnings details remain visible on the Earnings page. Useful when your
            screen might be seen by others.
          </small>

          <ToggleRow
            checked={hideEarnings}
            onChange={() => setHideEarnings(h => !h)}
            disabled={profileSaving}
            label={
              hideEarnings ? (
                <>
                  <i className="bi bi-eye-slash-fill" aria-hidden="true"
                     style={{ color: 'var(--tertiary-2-dark)', marginRight: '0.4rem' }} />
                  Earnings Hidden
                </>
              ) : (
                <>
                  <i className="bi bi-eye-fill" aria-hidden="true"
                     style={{ color: 'var(--tertiary-1-dark)', marginRight: '0.4rem' }} />
                  Earnings Visible
                </>
              )
            }
            description={
              hideEarnings
                ? 'Fee and payment info is hidden on the Dashboard and Gig List.'
                : 'Fee and payment info shows everywhere in the app.'
            }
          />
          <small style={{ marginTop: 'var(--space-2)' }}>
            <i className="bi bi-info-circle" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
            Click Save Profile below for this setting to take effect.
          </small>
        </div>

        {profileSaved  && <AlertBanner type="success">Profile saved.</AlertBanner>}
        {profileError  && <AlertBanner type="danger">{profileError}</AlertBanner>}

        <SaveButton onClick={saveProfile} saving={profileSaving}>
          Save Profile
        </SaveButton>

      </SectionCard>


      {/* ── THEMES ─────────────────────────────────────────── */}
      <SectionCard title="Themes">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-md)' }}>
          Choose a look that fits your vibe. Themes change the entire color palette and header
          font across the app and take effect instantly.
        </p>
        <ThemeSelector />
        <small style={{ marginTop: 'var(--space-3)', display: 'block' }}>
          Your theme is saved automatically when you pick a card.
        </small>
      </SectionCard>


      {/* ── 1. SOCIAL MEDIA ────────────────────────────────── */}
      <SectionCard title="Social Media">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-lg)' }}>
          These handles appear in the footer of your promo posters and on your public links page.
          Leave any blank and it will be skipped.
        </p>

        <HandleInput
          id="instagramInput"
          label={<><i className="bi bi-instagram" aria-hidden="true" style={{ marginRight: '0.4rem', color: '#e1306c' }} />Instagram</>}
          prefix="@"
          placeholder="yourhandle"
          value={instagram}
          onChange={setInstagram}
          disabled={profileSaving}
        />

        <HandleInput
          id="twitterXInput"
          label={<><i className="bi bi-twitter-x" aria-hidden="true" style={{ marginRight: '0.4rem' }} />X (Twitter)</>}
          prefix="@"
          placeholder="yourhandle"
          value={twitterX}
          onChange={setTwitterX}
          disabled={profileSaving}
        />

        <HandleInput
          id="tiktokInput"
          label={<><i className="bi bi-tiktok" aria-hidden="true" style={{ marginRight: '0.4rem' }} />TikTok</>}
          prefix="@"
          placeholder="yourhandle"
          value={tiktok}
          onChange={setTiktok}
          disabled={profileSaving}
        />

        <HandleInput
          id="blueskyInput"
          label={<><span className="brand-icon brand-icon--bluesky" style={{ color: '#0085ff', marginRight: '0.4rem' }} />Bluesky</>}
          prefix="@"
          placeholder="yourhandle.bsky.social"
          value={bluesky}
          onChange={setBluesky}
          disabled={profileSaving}
          maxLength={80}
        />

        <HandleInput
          id="youtubeInput"
          label={<><i className="bi bi-youtube" aria-hidden="true" style={{ marginRight: '0.4rem', color: '#ff0000' }} />YouTube</>}
          prefix="@"
          placeholder="yourchannel"
          value={youtube}
          onChange={setYoutube}
          disabled={profileSaving}
        />

        <HandleInput
          id="patreonInput"
          label={<><span className="brand-icon brand-icon--patreon" style={{ color: '#ff424d', marginRight: '0.4rem' }} />Patreon</>}
          prefix="@"
          placeholder="yourpage"
          value={patreon}
          onChange={setPatreon}
          disabled={profileSaving}
        />

        {profileSaved && <AlertBanner type="success">Profile and social handles saved.</AlertBanner>}
        {profileError && <AlertBanner type="danger">{profileError}</AlertBanner>}

        <SaveButton onClick={saveProfile} saving={profileSaving}>
          Save Profile and Socials
        </SaveButton>

      </SectionCard>


      {/* ── 2. PAYMENT AND TIPPING ─────────────────────────── */}
      <SectionCard title="Payment and Tipping">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-lg)' }}>
          These handles appear as one-tap tip buttons on your public links page.
          Fans can tap to open the app directly. Leave any blank to hide it.
        </p>

        <HandleInput
          id="cashappInput"
          label={<><span className="brand-icon brand-icon--cashapp" style={{ color: '#00d632', marginRight: '0.35rem' }} />Cash App</>}
          prefix="$"
          strip="$"
          placeholder="yourcashtag"
          value={cashapp}
          onChange={setCashapp}
          disabled={profileSaving}
        />

        <HandleInput
          id="venmoInput"
          label={<><span className="brand-icon brand-icon--venmo" style={{ color: '#3d95ce', marginRight: '0.35rem' }} />Venmo</>}
          prefix="@"
          placeholder="yourvenmo"
          value={venmo}
          onChange={setVenmo}
          disabled={profileSaving}
        />

        <HandleInput
          id="paypalInput"
          label={<><i className="bi bi-paypal" aria-hidden="true" style={{ marginRight: '0.35rem', color: '#0070ba' }} />PayPal</>}
          prefix="paypal.me/"
          strip="@"
          placeholder="yourhandle"
          value={paypal}
          onChange={setPaypal}
          disabled={profileSaving}
        />

        {profileSaved && <AlertBanner type="success">Payment handles saved.</AlertBanner>}
        {profileError && <AlertBanner type="danger">{profileError}</AlertBanner>}

        <SaveButton onClick={saveProfile} saving={profileSaving}>
          Save Payment Handles
        </SaveButton>

      </SectionCard>


      {/* ── 3. MY LINKS PAGE ───────────────────────────────── */}
      <SectionCard title="My Links Page">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-lg)' }}>
          Your public links page shows your upcoming shows, social media, website, and tip
          buttons. Share it as your bio link anywhere.
        </p>

        {/* Username picker */}
        <div className="form-group">
          <label htmlFor="usernameInput">
            <i className="bi bi-at" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
            Custom Username
          </label>
          <small style={{ marginBottom: 'var(--space-3)', display: 'block' }}>
            Pick a short, memorable username to make your link easy to share.
            Only lowercase letters, numbers, hyphens, and underscores. Min 3 characters.
          </small>

          {/* Username input */}
          <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
            <input
              id="usernameInput"
              type="text"
              placeholder="kingperk"
              value={username}
              onChange={handleUsernameChange}
              disabled={usernameSaving}
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              style={{
                borderRadius: 'var(--radius-md)',
                flex: 1,
                borderColor: usernameAvailable === true
                  ? 'var(--tertiary-1)'
                  : usernameAvailable === false
                    ? 'var(--tertiary-3)'
                    : undefined,
              }}
            />
            {/* Spinner while availability check is in-flight */}
            {usernameChecking && (
              <span style={{
                display:    'flex',
                alignItems: 'center',
                paddingLeft: '0.5rem',
              }}>
                <span
                  aria-label="Checking..."
                  style={{
                    display:      'inline-block',
                    width:        '14px',
                    height:       '14px',
                    border:       '2px solid rgba(101,2,94,0.25)',
                    borderTopColor: 'var(--neutral-1)',
                    borderRadius: '50%',
                    animation:    'spin 0.7s linear infinite',
                  }}
                />
              </span>
            )}
          </div>

          {/* Availability feedback */}
          {usernameAvailable === true && (
            <small style={{ color: 'var(--tertiary-1-dark)', fontWeight: 700, marginTop: 'var(--space-1)' }}>
              <i className="bi bi-check-circle-fill" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
              Available!
            </small>
          )}
          {usernameAvailable === false && (
            <small style={{ color: 'var(--tertiary-3-dark)', fontWeight: 700, marginTop: 'var(--space-1)' }}>
              <i className="bi bi-x-circle-fill" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
              Already taken. Try another.
            </small>
          )}
          {username.length > 0 && username.length < 3 && (
            <small style={{ color: 'var(--tertiary-2-dark)', fontWeight: 700, marginTop: 'var(--space-1)' }}>
              <i className="bi bi-exclamation-triangle" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
              Must be at least 3 characters.
            </small>
          )}

          {usernameError && (
            <AlertBanner type="danger" style={{ marginTop: 'var(--space-2)' }}>
              {usernameError}
            </AlertBanner>
          )}
          {usernameSaved && (
            <AlertBanner type="success" style={{ marginTop: 'var(--space-2)' }}>
              Username saved!
            </AlertBanner>
          )}

          <div style={{ marginTop: 'var(--space-3)' }}>
            <SaveButton
              onClick={saveUsername}
              saving={usernameSaving}
              disabled={
                username.trim().length < 3 ||
                usernameAvailable === false ||
                usernameChecking
              }
            >
              Save Username
            </SaveButton>
          </div>
        </div>

        <hr style={{ border: 'none', borderTop: '2px solid var(--accent-color)', margin: 'var(--spacing-lg) 0' }} />

        {/* Shareable link */}
        <div className="form-group">
          <label htmlFor="publicUrlDisplay">Your Fan Links URL</label>
          <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
            <input
              id="publicUrlDisplay"
              type="text"
              value={publicUrl}
              readOnly
              style={{
                borderRadius: 'var(--radius-md) 0 0 var(--radius-md)',
                fontFamily:   'monospace',
                fontSize:     '0.85rem',
                color:        'var(--neutral-1)',
                opacity:      0.8,
                flex:         1,
              }}
            />
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleCopyLink}
              style={{ borderRadius: '0 var(--radius-md) var(--radius-md) 0', borderLeft: 'none', whiteSpace: 'nowrap' }}
            >
              {linkCopied ? (
                <><i className="bi bi-check-lg" aria-hidden="true" style={{ marginRight: '0.3rem' }} />Copied!</>
              ) : (
                <><i className="bi bi-clipboard" aria-hidden="true" style={{ marginRight: '0.3rem' }} />Copy</>
              )}
            </button>
          </div>
          {!username && (
            <small>Set a username above for a cleaner link. Until then it uses your account ID.</small>
          )}
        </div>

        {/* Action buttons */}
        <div className="settings-fanlinks-actions" style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
          <a className="btn btn-primary" href="/links">
            <i className="bi bi-brush" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
            Customize Fan Links Page
          </a>
          <a
            className="btn btn-secondary"
            href={`/fanlinks/${publicSlug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
            View Fan Links Page
          </a>
        </div>

        <small style={{ marginTop: 'var(--space-3)', display: 'block' }}>
          Changes to stage name, social handles, and payment handles appear on your Fan Links page
          immediately after saving above.
        </small>

      </SectionCard>


      {/* ── 4. GIG NOTIFICATIONS ───────────────────────────── */}
      <SectionCard title="Gig Notifications">

        {/* Browser not supported */}
        {!isSupported && (
          <AlertBanner type="warning">
            Push notifications are not supported in this browser.
            {isIOS
              ? ' On iPhone or iPad, install this app to your home screen first: tap the Share button, then "Add to Home Screen". Then reopen the app and come back here.'
              : ' Try Chrome or Firefox for push notification support.'
            }
          </AlertBanner>
        )}

        {/* Permission denied */}
        {isSupported && permission === 'denied' && (
          <AlertBanner type="danger">
            Notifications are blocked for this site. Open your browser site settings,
            find this site, and change the notifications permission to Allow. Then reload.
          </AlertBanner>
        )}

        {pushError && <AlertBanner type="danger">{pushError}</AlertBanner>}

        {isSupported && permission !== 'denied' && (
          <>
            {/* iOS install prompt */}
            {isIOS && !isStandalone && !isSubscribed && (
              <AlertBanner type="info">
                <strong>iPhone / iPad users:</strong> Push notifications on iOS require the app
                to be installed to your home screen first.
                <ol style={{ marginBottom: 0, marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                  <li>Tap the Share button <i className="bi bi-share" aria-hidden="true" /> in Safari</li>
                  <li>Tap Add to Home Screen</li>
                  <li>Open the app from your home screen</li>
                  <li>Come back to Settings and turn on notifications</li>
                </ol>
              </AlertBanner>
            )}

            {/* On/off toggle */}
            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
              <ToggleRow
                checked={isSubscribed}
                disabled={pushLoading || (isIOS && !isStandalone)}
                onChange={() => {
                  if (isIOS && !isStandalone) return
                  isSubscribed ? unsubscribe() : subscribe()
                }}
                label={
                  isSubscribed ? (
                    <>
                      <i className="bi bi-check-circle-fill" aria-hidden="true"
                         style={{ color: 'var(--tertiary-1-dark)', marginRight: '0.4rem' }} />
                      Notifications On
                    </>
                  ) : 'Enable Gig Reminders'
                }
                description={
                  isSubscribed
                    ? "You'll get Get Ready and Leave Now alerts even when the app is closed."
                    : 'Get reminders before your gigs on this device.'
                }
              />
            </div>

            {/* Loading feedback */}
            {pushLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                            color: 'var(--neutral-1)', opacity: 0.65,
                            fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-md)' }}>
                <span
                  aria-hidden="true"
                  style={{ display: 'inline-block', width: '14px', height: '14px',
                           border: '2px solid rgba(101,2,94,0.25)', borderTopColor: 'var(--neutral-1)',
                           borderRadius: '50%', animation: 'spin 0.7s linear infinite' }}
                />
                {isSubscribed ? 'Disabling...' : 'Enabling push notifications...'}
              </div>
            )}

            {/* Test notification */}
            {isSubscribed && !pushLoading && (
              <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                <button className="btn btn-secondary" onClick={sendTestNotification}>
                  <i className="bi bi-send" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
                  Send Test Notification
                </button>
                <small style={{ marginTop: 'var(--space-2)', display: 'block' }}>
                  Fires a real push through the full server pipeline so you can confirm delivery
                  works on this device.
                </small>
              </div>
            )}

            <hr style={{ border: 'none', borderTop: '2px solid var(--accent-color)', margin: 'var(--spacing-lg) 0' }} />

            {/* ── GET READY TIMER ─── */}
            <div className="form-group">
              <label>
                <i className="bi bi-clock" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
                How long does it take you to get ready? 
              </label>
              <small style={{ marginBottom: 'var(--space-3)', display: 'block' }}>
                And Be honest with yourself about it.
              </small>
              <small style={{ marginBottom: 'var(--space-3)', display: 'block' }}>
                A Get Ready notification fires this long before your gig starts.
              </small>
              <NumberPairInput
                hoursValue={prepHours}
                minsValue={prepMins}
                onHoursChange={handlePrepHoursChange}
                onMinsChange={handlePrepMinsChange}
                maxHours={8}
                disabled={prefsLoading}
              />
              {prepMinutes > 0 && (
                <small style={{ marginTop: 'var(--space-2)' }}>
                  Get Ready fires{' '}
                  {prepHours > 0 && <strong>{prepHours}h </strong>}
                  {prepMins  > 0 && <strong>{prepMins}m </strong>}
                  before each gig.
                </small>
              )}
            </div>

            {/* ── ADVANCE WARNING ─── */}
            <div className="form-group">
              <label>
                <i className="bi bi-bell" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
                How far in advance do you want a warning?
              </label>
              <small style={{ marginBottom: 'var(--space-3)', display: 'block' }}>
                You'll get an early heads-up this long before each Get Ready, Leave Home, and
                Leave Now notification.
              </small>
              <NumberPairInput
                hoursValue={advanceHours}
                minsValue={advanceMins}
                onHoursChange={handleAdvanceHoursChange}
                onMinsChange={handleAdvanceMinsChange}
                maxHours={2}
                disabled={prefsLoading}
              />
              {advanceNoticeMinutes > 0 ? (
                <small style={{ marginTop: 'var(--space-2)' }}>
                  Warning fires{' '}
                  {advanceHours > 0 && <strong>{advanceHours}h </strong>}
                  {advanceMins  > 0 && <strong>{advanceMins}m </strong>}
                  before each reminder.
                </small>
              ) : (
                <small style={{ marginTop: 'var(--space-2)' }}>
                  No advance warnings. Notifications fire at the exact scheduled times only.
                </small>
              )}
            </div>

            {/* ── HOME LOCATION ─── */}
            <div className="form-group">
              <label>
                <i className="bi bi-house" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
                Where do you travel from?
              </label>
              <small style={{ marginBottom: 'var(--space-3)', display: 'block' }}>
                Drive time from here to the venue determines your Leave Now alert.
                A {LEAVE_BUFFER_MINUTES}-minute buffer is added on top of the routing result.
              </small>

              {/* Address search row */}
              <div className="settings-address-row" style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--space-3)' }}>
                <input
                  type="text"
                  placeholder="123 Main St, Charlotte, NC"
                  value={homeAddress}
                  onChange={e => setHomeAddress(e.target.value)}
                  disabled={prefsLoading || geocoding}
                  onKeyDown={e => e.key === 'Enter' && geocodeAndPreview()}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={geocodeAndPreview}
                  disabled={prefsLoading || geocoding || !homeAddress.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {geocoding ? (
                    <>
                      <span
                        aria-hidden="true"
                        style={{ display: 'inline-block', width: '12px', height: '12px',
                                 border: '2px solid rgba(101,2,94,0.25)', borderTopColor: 'var(--neutral-1)',
                                 borderRadius: '50%', animation: 'spin 0.7s linear infinite',
                                 marginRight: '0.35rem' }}
                      />
                      Looking up...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-search" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
                      Find Address
                    </>
                  )}
                </button>
              </div>

              {/* Current location button */}
              <button
                className="btn btn-secondary settings-location-btn"
                onClick={useCurrentLocation}
                disabled={prefsLoading || geocoding}
                style={{ marginBottom: 'var(--spacing-md)' }}
              >
                <i className="bi bi-geo-alt" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
                Use My Current Location
              </button>

              {/* Geocode confirmed badge */}
              {geocodedLabel && homeLat && homeLng && (
                <AlertBanner type="success">
                  <strong>Location confirmed:</strong>{' '}{geocodedLabel}
                </AlertBanner>
              )}

              {/* Nudge if address typed but not geocoded */}
              {homeAddress && !homeLat && !geocoding && (
                <small>
                  <i className="bi bi-arrow-up" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
                  Click Find Address or drag the pin on the map below.
                </small>
              )}

              {/* Map picker */}
              <div style={{ marginTop: 'var(--spacing-md)' }}>
                <MapPicker lat={homeLat} lng={homeLng} onLocationChange={handleMapLocationChange} />
              </div>
            </div>

            {prefsError  && <AlertBanner type="danger">{prefsError}</AlertBanner>}
            {saveSuccess && <AlertBanner type="success">Notification settings saved.</AlertBanner>}

            <SaveButton
              onClick={savePrefs}
              saving={prefsSaving}
              disabled={prefsLoading || !homeLat || !homeLng}
            >
              Save Notification Settings
            </SaveButton>

            {!homeLat && !homeLng && !prefsLoading && (
              <small style={{ marginTop: 'var(--space-2)', display: 'block' }}>
                Set your home location on the map above to enable saving.
              </small>
            )}
          </>
        )}
      </SectionCard>


      {/* ── HOW IT WORKS (shown only when fully configured) ── */}
      {isSubscribed && homeLat && homeLng && (
        <div style={{
          background:    'var(--neutral-bg)',
          border:        '2px solid var(--accent-color)',
          borderRadius:  'var(--radius-card)',
          padding:       'var(--spacing-md)',
          marginBottom:  'var(--spacing-lg)',
          fontSize:      'var(--text-sm)',
          color:         'var(--neutral-1)',
        }}>
          <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
            <i className="bi bi-info-circle" aria-hidden="true" style={{ marginRight: '0.35rem', color: 'var(--primary)' }} />
            How your reminders work
          </strong>
          <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.8 }}>
            <li>
              <strong>Get Ready</strong> fires{' '}
              {prepHours > 0 && <>{prepHours}h </>}
              {prepMins  > 0 && <>{prepMins}m </>}
              {prepMinutes === 0 && 'right at '}
              before your scheduled leave time.
            </li>
            <li style={{ marginTop: '0.25rem' }}>
              <strong>Leave Now</strong> fires based on your real drive time from home to the
              venue (via OpenStreetMap routing) plus a {LEAVE_BUFFER_MINUTES}-minute buffer.
            </li>
            <li style={{ marginTop: '0.25rem' }}>
              Gigs need a <strong>start time</strong> and <strong>venue address</strong> to
              receive reminders. Notifications arrive even when the app is closed.
            </li>
          </ul>
        </div>
      )}


      {/* ── 8. PERFORMER ID ────────────────────────────────── */}
      <SectionCard title="Performer ID">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-md)' }}>
          Share this ID with producers using the Producer Pocket Planner so they can add you to
          shows and send booking requests.
        </p>

        <div className="form-group">
          <label htmlFor="performerIdDisplay">Your Performer ID</label>
          <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
            <input
              id="performerIdDisplay"
              type="text"
              value={userID}
              readOnly
              style={{
                borderRadius: 'var(--radius-md) 0 0 var(--radius-md)',
                fontFamily:   'monospace',
                fontSize:     '0.82rem',
                letterSpacing: '0.03em',
                color:        'var(--neutral-1)',
                opacity:      0.8,
                flex:         1,
              }}
            />
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleCopyID}
              style={{ borderRadius: '0 var(--radius-md) var(--radius-md) 0', whiteSpace: 'nowrap' }}
            >
              {idCopied ? (
                <><i className="bi bi-check-lg" aria-hidden="true" style={{ marginRight: '0.3rem' }} />Copied!</>
              ) : (
                <><i className="bi bi-clipboard" aria-hidden="true" style={{ marginRight: '0.3rem' }} />Copy ID</>
              )}
            </button>
          </div>
          <small>
            <i className="bi bi-shield-lock" aria-hidden="true" style={{ marginRight: '0.3rem' }} />
            This ID only links your account to the performer profile producers will use on the upcoming Producer Pocket Planner.
            It does not expose any personal information like your earnings or upcoming gigs.
          </small>
        </div>
      </SectionCard>


      {/* ── 9. ABOUT AND LEGAL ─────────────────────────────── */}
      <SectionCard title="About and Legal">
        {/* Info rows */}
        {[
          {
            icon:  'bi-hash',
            label: 'App Version',
            right: <span style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.6 }}>1.0.0</span>,
          },
        ].map(row => (
          <div key={row.label} style={{
            display:        'flex',
            justifyContent: 'space-between',
            alignItems:     'center',
            padding:        'var(--space-3) 0',
            borderBottom:   '1px solid var(--accent-color)',
          }}>
            <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--neutral-1)' }}>
              <i className={`bi ${row.icon}`} aria-hidden="true" style={{ marginRight: '0.4rem', opacity: 0.5 }} />
              {row.label}
            </span>
            {row.right}
          </div>
        ))}

        {/* About link */}
        <Link
          to="/about"
          style={{
            display:        'flex',
            alignItems:     'center',
            gap:            '0.5rem',
            padding:        'var(--space-3) 0',
            borderBottom:   '1px solid var(--accent-color)',
            textDecoration: 'none',
            color:          'var(--neutral-1)',
          }}
        >
          <i className="bi bi-info-circle" aria-hidden="true" style={{ opacity: 0.5 }} />
          <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', flex: 1 }}>
            About Performer Pocket Planner
          </span>
          <i className="bi bi-chevron-right" aria-hidden="true" style={{ opacity: 0.4 }} />
        </Link>

        {/* Privacy link */}
        <Link
          to="/privacy"
          style={{
            display:        'flex',
            alignItems:     'center',
            gap:            '0.5rem',
            padding:        'var(--space-3) 0',
            textDecoration: 'none',
            color:          'var(--neutral-1)',
          }}
        >
          <i className="bi bi-shield-lock" aria-hidden="true" style={{ opacity: 0.5 }} />
          <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)', flex: 1 }}>
            Privacy Policy
          </span>
          <i className="bi bi-chevron-right" aria-hidden="true" style={{ opacity: 0.4 }} />
        </Link>
      </SectionCard>


      {/* ── 10. ACCOUNT / SIGN OUT ─────────────────────────── */}
      <section style={{
        background:    'var(--brand-white)',
        border:        '3px solid var(--tertiary-3)',
        borderRadius:  'var(--radius-card)',
        marginBottom:  'var(--spacing-lg)',
        overflow:      'hidden',
      }}>
        <div style={{
          padding:      'var(--space-4) var(--spacing-lg)',
          borderBottom: '2px solid var(--tertiary-3-light)',
          borderLeft:   '5px solid var(--tertiary-3)',
          background:   'rgba(255,68,68,0.06)',
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize:   'var(--text-2xl)',
            color:      'var(--neutral-1)',
            margin:     0,
          }}>
            Account
          </h2>
        </div>
        <div style={{ padding: 'var(--spacing-lg)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.7, marginBottom: 'var(--spacing-md)' }}>
            Signing out will clear your session on this device. Your gigs, earnings, and settings
            are saved to your account and will be waiting when you sign back in.
          </p>
          <button
            className="btn btn-danger"
            type="button"
            onClick={async () => {
              await supabase.auth.signOut()
              window.location.href = '/'
            }}
          >
            <i className="bi bi-box-arrow-right" aria-hidden="true" style={{ marginRight: '0.35rem' }} />
            Sign Out
          </button>
        </div>
      </section>

    </div>
  )
}